/**
 * Monitoradores — a tela que põe um número de WhatsApp no ar.
 *
 * Duas decisões que valem o comentário:
 *
 *  - **"Monitorador" é jargão nosso.** Ninguém chega aqui sabendo o que é, e
 *    por isso a definição aparece no cabeçalho e, inteira, no estado vazio.
 *    Tela de produto que estreia um termo sem explicá-lo transfere ao usuário
 *    um trabalho que era nosso.
 *  - **O limite do plano vem avaliado do servidor** (`list.limit`). O cliente
 *    lê `allowed`/`remaining` e pinta; não conta monitorador, não compara com
 *    número nenhum. Invariante 4 do CLAUDE.md.
 *
 * Grade de cartões (UI kit do export, out/2026): um por número, nas duas
 * larguras — no celular a grade vira uma coluna. `monitoradores` está na barra
 * inferior, é tela que se olha em pé, com uma mão (seção 11 do DESIGN_SYSTEM).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  ActionMenu,
  Avatar,
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Separator,
  Skeleton,
  SkeletonText,
  StatusPill,
  useToast,
} from '@mgdlead/ui';
import type { ActionMenuItem } from '@mgdlead/ui';
import { Archive, Info, Pencil, Plug, Plus, QrCode, Radio, Trash2, Unplug } from 'lucide-react';
import { can } from '@mgdlead/shared';

import { FormAlert } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import { GuiaDeConexao } from '../../../features/guias/GuiaDeConexao';
import { GUIA_WHATSAPP } from '../../../features/guias/conexoes';
import { MonitorDetailDrawer } from './MonitorDetailDrawer';
import { MonitorNameDialog } from './MonitorNameDialog';
import { QrDrawer } from './QrDrawer';
import { UptimeLine } from './UptimeLine';
import {
  CONNECTION_ACTION_LABEL,
  MONITOR_HEALTH,
  connectionActionFor,
  monitorHasHistory,
  monitorRemovalBlocker,
} from '../../../features/monitor/monitor-ui';
import type { MonitorConnectionAction } from '../../../features/monitor/monitor-ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import {
  useArchiveMonitor,
  useConnectMonitor,
  useCreateMonitor,
  useDisconnectMonitor,
  useMonitors,
  useReconnectMonitor,
  useRemoveMonitor,
  useRenameMonitor,
} from '../../../lib/api';
import type { Monitor, MonitorConnectResult, MonitorLimit } from '../../../lib/api';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

/** A frase que ensina o termo. Uma só, usada em dois lugares. */
const O_QUE_E =
  'Um monitorador é um número de WhatsApp que fica conectado ao MGDLead e registra quem entra e quem sai dos seus grupos.';

type QrTarget = { id: string; name: string };
type ConfirmKind = 'disconnect' | 'archive' | 'remove';
type Confirm = { kind: ConfirmKind; monitor: Monitor };
type NameDialog = { mode: 'create' } | { mode: 'rename'; monitor: Monitor };

export function MonitorsPage() {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  useDocumentTitle('Monitoradores');

  const [includeArchived, setIncludeArchived] = useState(false);
  const monitorsQuery = useMonitors(workspace.id, includeArchived);

  const [nameDialog, setNameDialog] = useState<NameDialog | null>(null);
  const [qrTarget, setQrTarget] = useState<QrTarget | null>(null);
  const [detail, setDetail] = useState<Monitor | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const create = useCreateMonitor();
  const rename = useRenameMonitor();
  const archive = useArchiveMonitor();
  const remove = useRemoveMonitor();
  const connect = useConnectMonitor();
  const reconnect = useReconnectMonitor();
  const disconnect = useDisconnectMonitor();

  const canCreate = can(role, 'monitor:create');
  const canConnect = can(role, 'monitor:connect');
  const canUpdate = can(role, 'monitor:update');
  const canDelete = can(role, 'monitor:delete');

  const data = monitorsQuery.data;
  const monitors = useMemo(() => data?.monitors ?? [], [data]);
  const limit: MonitorLimit | undefined = data?.limit;
  /* Servidor já respondeu "cabe mais um?". Aqui só se LÊ a resposta. */
  const limitReached = limit !== undefined && !limit.unlimited && !limit.allowed;

  /** Depois de conectar/reconectar: ou abre o QR, ou conta o que aconteceu. */
  const afterConnect = useCallback(
    (monitor: Monitor, result: MonitorConnectResult) => {
      if (result.needsQr) {
        setQrTarget({ id: monitor.id, name: monitor.name });
        return;
      }
      if (result.status === 'CONNECTED') {
        toast({
          title: 'Monitorador conectado',
          description: `${monitor.name} está no ar.`,
          tone: 'success',
        });
        return;
      }
      if (result.status === 'ERROR') {
        setRowError(
          `Não conseguimos conectar ${monitor.name}. Abra os detalhes para ver o último erro.`,
        );
        return;
      }
      // CONNECTING: o provider ainda está subindo — o QR aparece de lá a pouco.
      setQrTarget({ id: monitor.id, name: monitor.name });
    },
    [toast],
  );

  const runConnection = useCallback(
    (monitor: Monitor, action: MonitorConnectionAction) => {
      setRowError(null);
      const onError = (error: unknown) => setRowError(humanizeError(error));

      switch (action) {
        case 'qr':
          setQrTarget({ id: monitor.id, name: monitor.name });
          return;
        case 'connect':
          connect.mutate(
            { workspaceId: workspace.id, monitorId: monitor.id },
            { onSuccess: (result) => afterConnect(monitor, result), onError },
          );
          return;
        case 'reconnect':
          reconnect.mutate(
            { workspaceId: workspace.id, monitorId: monitor.id },
            { onSuccess: (result) => afterConnect(monitor, result), onError },
          );
          return;
        case 'disconnect':
          // Desconectar derruba a captura: nunca sem perguntar.
          setConfirm({ kind: 'disconnect', monitor });
          return;
      }
    },
    [afterConnect, connect, reconnect, workspace.id],
  );

  /** Criar já emenda em conectar: um monitorador que não conecta não serve. */
  const handleCreate = useCallback(
    async (name: string) => {
      const created = await create.mutateAsync({ workspaceId: workspace.id, name });
      /**
       * Espera o `connect` ANTES de abrir a janela do QR.
       *
       * Abrindo antes, o polling começava enquanto a sessão ainda não existia no
       * provider, recebia `NOT_STARTED` e parava — o QR nunca aparecia, e o
       * usuário via "a conexão não está ativa" num monitorador recém-criado.
       */
      try {
        await connect.mutateAsync({ workspaceId: workspace.id, monitorId: created.id });
      } catch (error: unknown) {
        setRowError(humanizeError(error));
        return;
      }
      toast({
        title: 'Monitorador criado',
        description: `Leia o código com ${created.name}.`,
        tone: 'success',
      });
      setQrTarget({ id: created.id, name: created.name });
    },
    [connect, create, toast, workspace.id],
  );

  const handleRename = useCallback(
    async (monitor: Monitor, name: string) => {
      await rename.mutateAsync({ workspaceId: workspace.id, monitorId: monitor.id, name });
      toast({ title: 'Nome atualizado', tone: 'success' });
    },
    [rename, toast, workspace.id],
  );

  const rowProps = {
    timezone: workspace.timezone,
    canConnect,
    canUpdate,
    canDelete,
    onOpenDetail: setDetail,
    onConnection: runConnection,
    onRename: (monitor: Monitor) => setNameDialog({ mode: 'rename', monitor }),
    onArchive: (monitor: Monitor) => setConfirm({ kind: 'archive', monitor }),
    onRemove: (monitor: Monitor) => setConfirm({ kind: 'remove', monitor }),
  };

  return (
    <>
      <PageHeader
        title="Monitoradores"
        description={`${O_QUE_E} Um mesmo monitorador acompanha vários grupos ao mesmo tempo.`}
        action={
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus />}
              leadingBubble
              block
              disabled={limitReached}
              onClick={() => setNameDialog({ mode: 'create' })}
            >
              Adicionar monitorador
            </Button>
          ) : null
        }
      />

      {limitReached && limit !== undefined ? (
        <FormAlert tone="info" className="mb-3">
          Seu plano permite {limit.limit} {limit.limit === 1 ? 'monitorador' : 'monitoradores'} e
          você já está com {limit.current}. Arquive um número ou mude de plano para adicionar outro.
        </FormAlert>
      ) : null}

      {rowError !== null ? <FormAlert className="mb-3">{rowError}</FormAlert> : null}

      {monitorsQuery.isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Card key={i} aria-hidden="true">
              <div className="flex items-start gap-3">
                <Skeleton className="size-10 shrink-0" radius="full" />
                <div className="flex flex-1 flex-col gap-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
              <Skeleton className="mt-4 h-5 w-24" radius="full" />
              <Separator className="my-4" />
              <SkeletonText lines={2} />
            </Card>
          ))}
        </div>
      ) : monitorsQuery.isError ? (
        <Card>
          <ErrorState
            title="Não conseguimos carregar os monitoradores"
            description={humanizeError(monitorsQuery.error)}
            onRetry={() => void monitorsQuery.refetch()}
          />
        </Card>
      ) : monitors.length === 0 ? (
        <Card>
          <EmptyState
            kind="no-data"
            icon={Radio}
            title="Nenhum monitorador ainda"
            description={
              <>
                {O_QUE_E} Pense nele como um aparelho, não como um grupo: você conecta o chip uma
                vez, lendo um QR code, e a partir daí ele acompanha todos os grupos que você
                escolher. Enquanto não houver nenhum conectado, o painel não tem de onde tirar
                número.
              </>
            }
            action={
              canCreate ? (
                <Button
                  variant="primary"
                  leadingIcon={<Plus />}
                  leadingBubble
                  disabled={limitReached}
                  onClick={() => setNameDialog({ mode: 'create' })}
                  className="max-md:w-full"
                >
                  Conectar o primeiro monitorador
                </Button>
              ) : null
            }
          />
        </Card>
      ) : (
        /*
          Um cartão por número, como no UI kit do export: monitorador é APARELHO, e aparelho
          se lê de relance (quem é, em que pé está, quando falou por último) — não como linha
          de planilha. A ação de conexão que o estado pede fica visível e larga; o resto mora
          no "…".
        */
        <ul
          aria-label={`Monitoradores de ${workspace.name}`}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          {monitors.map((monitor) => (
            <li key={monitor.id} className="min-w-0">
              <MonitorCard monitor={monitor} {...rowProps} />
            </li>
          ))}
        </ul>
      )}

      {(data?.archivedCount ?? 0) > 0 ? (
        <div className="mt-3 flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIncludeArchived((atual) => !atual)}
            className="max-md:w-full"
          >
            {includeArchived
              ? 'Ocultar arquivados'
              : `Mostrar arquivados (${data?.archivedCount ?? 0})`}
          </Button>
        </div>
      ) : null}

      {/*
        O guia de conexão. Sem nenhum número conectado ele vem ABERTO — é o caminho inteiro,
        e a parte que importa ("Se não funcionar") é a que explica por que reconectar não
        traz de volta quem entrou enquanto o número estava fora. Com número já conectado ele
        encolhe para uma linha, mas não some: é para esta tela que a pessoa volta quando o
        monitorador cai.

        Só depois que a consulta responde: enquanto ela está no ar, `monitors.length` é zero
        por falta de resposta, e o guia abriria para fechar em seguida (invariante 12 — não
        ter resposta não é o mesmo que não ter monitorador).
      */}
      {monitorsQuery.isSuccess ? (
        <GuiaDeConexao guia={GUIA_WHATSAPP} recolhido={monitors.length > 0} className="mt-4" />
      ) : null}

      {/* ------------------------------ diálogos ------------------------------ */}

      <MonitorNameDialog
        open={nameDialog !== null}
        onOpenChange={(open) => {
          if (!open) setNameDialog(null);
        }}
        mode={nameDialog?.mode ?? 'create'}
        initialName={nameDialog?.mode === 'rename' ? nameDialog.monitor.name : ''}
        onSubmit={async (name) => {
          if (nameDialog === null) return;
          if (nameDialog.mode === 'create') await handleCreate(name);
          else await handleRename(nameDialog.monitor, name);
        }}
      />

      <QrDrawer
        open={qrTarget !== null}
        workspaceId={workspace.id}
        monitorId={qrTarget?.id ?? null}
        monitorName={qrTarget?.name ?? 'monitorador'}
        onOpenChange={(open) => {
          if (!open) setQrTarget(null);
        }}
        onConnected={() => void monitorsQuery.refetch()}
      />

      <MonitorDetailDrawer
        open={detail !== null}
        workspaceId={workspace.id}
        timezone={workspace.timezone}
        monitor={detail}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      />

      <ConfirmDialog
        confirm={confirm}
        onClose={() => setConfirm(null)}
        pending={disconnect.isPending || archive.isPending || remove.isPending}
        onConfirm={(target) => {
          const input = { workspaceId: workspace.id, monitorId: target.monitor.id };
          const onError = (error: unknown) => setRowError(humanizeError(error));
          const done = (title: string, description?: string) => {
            toast({
              title,
              ...(description !== undefined ? { description } : {}),
              tone: 'success',
            });
            setConfirm(null);
          };

          if (target.kind === 'disconnect') {
            disconnect.mutate(input, {
              onSuccess: () => done('Monitorador desconectado'),
              onError,
            });
          } else if (target.kind === 'archive') {
            archive.mutate(input, {
              onSuccess: () => {
                /**
                 * Passa a mostrar os arquivados na hora.
                 *
                 * Sem isto, quem arquivou para liberar a vaga do plano vê o
                 * monitorador simplesmente SUMIR da tela: o caminho para excluir
                 * de vez fica atrás de um botão que ninguém tem motivo para
                 * procurar naquele segundo. Foi assim que "não tem botão de
                 * excluir monitorador" nasceu.
                 */
                setIncludeArchived(true);
                done(
                  'Monitorador arquivado',
                  `${target.monitor.name} continua na lista, marcado como arquivado.`,
                );
              },
              onError,
            });
          } else {
            remove.mutate(input, {
              onSuccess: (result) =>
                done(
                  'Monitorador excluído',
                  result.keptHistory
                    ? `${target.monitor.name} saiu da lista. O histórico de entradas e saídas continua inteiro.`
                    : `${target.monitor.name} e a conexão dele com o WhatsApp foram apagados.`,
                ),
              onError,
            });
          }
        }}
      />
    </>
  );
}

/* ========================================================================== */
/*  Cartão                                                                     */
/* ========================================================================== */

interface RowProps {
  monitor: Monitor;
  timezone: string;
  canConnect: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  onOpenDetail: (monitor: Monitor) => void;
  onConnection: (monitor: Monitor, action: MonitorConnectionAction) => void;
  onRename: (monitor: Monitor) => void;
  onArchive: (monitor: Monitor) => void;
  onRemove: (monitor: Monitor) => void;
}

/** Contorno do cartão pelo estado: erro e QR pendente saltam da grade (2º canal: a pill). */
const CARD_TONE: Partial<Record<Monitor['status'], 'danger' | 'warning'>> = {
  ERROR: 'danger',
  QR_REQUIRED: 'warning',
};

function MonitorCard(props: RowProps) {
  const { monitor, timezone, onOpenDetail } = props;
  const archived = monitor.archivedAt !== null;
  const tone = archived ? undefined : CARD_TONE[monitor.status];

  return (
    <Card tone={tone ?? 'default'} className="flex h-full flex-col">
      <div className="flex items-start gap-3">
        <Avatar name={monitor.name} size="lg" />
        <div className="min-w-0 flex-1">
          <NameButton monitor={monitor} onOpenDetail={onOpenDetail} />
          <PhoneLine monitor={monitor} />
        </div>
        <MonitorMenu {...props} />
      </div>

      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        {archived ? (
          <Badge tone="neutral" dot>
            Arquivado
          </Badge>
        ) : (
          <StatusPill status={monitor.status} />
        )}
      </div>

      <Separator className="my-4" />

      <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-muted-foreground">Grupos monitorados</dt>
        <dd className="nums text-right font-semibold">{monitor.groupsCount}</dd>
        <dt className="text-muted-foreground">Último evento</dt>
        <dd className="text-right font-semibold">
          <LastEvent monitor={monitor} timezone={timezone} />
        </dd>
        {archived ? null : (
          <>
            <dt className="text-muted-foreground">Captura</dt>
            <dd className="text-right">
              <HealthBadge monitor={monitor} />
            </dd>
          </>
        )}
      </dl>

      {/* D6: "Conectado 98,7% dos últimos 30 dias", pronto do servidor. */}
      {archived ? null : <UptimeLine monitorId={monitor.id} />}

      <CardAction {...props} />
    </Card>
  );
}

/**
 * A ação que o ESTADO pede, larga e visível (Reconectar/Ver QR/Conectar). Desconectar não
 * mora aqui: é a ação que derruba a captura, e por isso fica no "…" — nunca a um toque
 * distraído do polegar. Sem permissão, o botão não existe (quem barra é o servidor).
 */
function CardAction({ monitor, canConnect, canDelete, onConnection, onRemove }: RowProps) {
  const archived = monitor.archivedAt !== null;

  if (archived) {
    if (!canDelete) return null;
    /**
     * O botão aparece para TODO arquivado (pedido do dono, 11/10/2026). Com grupo ou
     * evento preso, o servidor exclui sem tocar no histórico — e a frase embaixo diz
     * isso antes do clique, para ninguém deixar de excluir por medo de perder dado.
     */
    const blocker = monitorRemovalBlocker(monitor);
    const note =
      blocker ??
      (monitorHasHistory(monitor) ? 'O histórico de entradas e saídas fica guardado.' : null);
    return (
      <div className="mt-4 flex flex-col gap-1.5">
        <Button
          block
          variant="danger-soft"
          leadingIcon={<Trash2 />}
          aria-label={`Excluir ${monitor.name}`}
          disabled={blocker !== null}
          onClick={() => onRemove(monitor)}
        >
          Excluir
        </Button>
        {note !== null ? <span className="text-2xs text-muted-foreground">{note}</span> : null}
      </div>
    );
  }

  const action = connectionActionFor(monitor.status);
  if (!canConnect || action === 'disconnect') return null;

  return (
    <div className="mt-4">
      <Button
        block
        variant={action === 'connect' ? 'secondary' : 'primary'}
        leadingIcon={action === 'connect' ? <Plug /> : <QrCode />}
        aria-label={`${CONNECTION_ACTION_LABEL[action]} ${monitor.name}`}
        onClick={() => onConnection(monitor, action)}
      >
        {CONNECTION_ACTION_LABEL[action]}
      </Button>
    </div>
  );
}

/** O "…": detalhes, renomear, desconectar e arquivar — o que o papel permite. */
function MonitorMenu({
  monitor,
  canConnect,
  canUpdate,
  canDelete,
  onOpenDetail,
  onConnection,
  onRename,
  onArchive,
}: RowProps) {
  const archived = monitor.archivedAt !== null;
  const items: ActionMenuItem[] = [
    { id: 'detail', label: 'Ver detalhes', icon: <Info />, onSelect: () => onOpenDetail(monitor) },
  ];

  if (!archived) {
    if (canUpdate) {
      items.push({
        id: 'rename',
        label: 'Renomear',
        icon: <Pencil />,
        onSelect: () => onRename(monitor),
      });
    }
    if (canConnect && connectionActionFor(monitor.status) === 'disconnect') {
      items.push({
        id: 'disconnect',
        label: 'Desconectar',
        icon: <Unplug />,
        onSelect: () => onConnection(monitor, 'disconnect'),
      });
    }
    if (canDelete) {
      items.push({
        id: 'archive',
        label: 'Arquivar',
        icon: <Archive />,
        destructive: true,
        separated: true,
        onSelect: () => onArchive(monitor),
      });
    }
  }

  return <ActionMenu items={items} label={`Ações de ${monitor.name}`} />;
}

function NameButton({ monitor, onOpenDetail }: Pick<RowProps, 'monitor' | 'onOpenDetail'>) {
  return (
    <button
      type="button"
      onClick={() => onOpenDetail(monitor)}
      className="block max-w-full truncate text-left text-md font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {monitor.name}
    </button>
  );
}

function PhoneLine({ monitor }: Pick<RowProps, 'monitor'>) {
  return (
    <span className="nums mt-0.5 block truncate font-mono text-xs text-subtle-foreground">
      {monitor.phoneE164 ?? 'Telefone ainda desconhecido'}
    </span>
  );
}

function HealthBadge({ monitor }: Pick<RowProps, 'monitor'>) {
  const spec = MONITOR_HEALTH[monitor.health];
  return (
    <Badge tone={spec.tone} dot title={spec.description}>
      {spec.label}
    </Badge>
  );
}

/**
 * Relativo na tela, exato no `title`. E "nunca houve evento" não é "há muito
 * tempo": o travessão diz ausência, que é outra informação (invariante 8).
 */
function LastEvent({
  monitor,
  timezone,
  prefix = '',
}: Pick<RowProps, 'monitor' | 'timezone'> & { prefix?: string }) {
  const relative = formatRelative(monitor.lastEventAt);
  if (relative === null) {
    return <span className="text-muted-foreground">{prefix}Nenhum até agora</span>;
  }

  const exact = formatExact(monitor.lastEventAt, timezone);
  return (
    <span {...(exact !== null ? { title: exact } : {})}>
      {prefix}
      {relative}
    </span>
  );
}

/* ========================================================================== */
/*  Confirmação                                                                */
/* ========================================================================== */

const CONFIRM_COPY: Record<
  ConfirmKind,
  { title: string; label: string; body: (name: string) => string }
> = {
  disconnect: {
    title: 'Desconectar monitorador',
    label: 'Desconectar',
    body: (name) =>
      `${name} sai do ar na hora e para de registrar entradas e saídas. Para voltar, será preciso ler o QR code de novo no celular.`,
  },
  archive: {
    title: 'Arquivar monitorador',
    label: 'Arquivar',
    body: (name) =>
      `${name} sai da operação e libera a vaga do seu plano na hora. O histórico já capturado continua inteiro: arquivar não apaga nada. Ele fica na lista marcado como arquivado, e é de lá que se exclui de vez. Hoje não existe desarquivar — para voltar com este número, é criar outro monitorador e ler o QR code de novo.`,
  },
  remove: {
    title: 'Excluir monitorador',
    label: 'Excluir definitivamente',
    body: (name) =>
      `${name} sai da sua lista de vez, e a conexão dele com o WhatsApp é apagada junto. O histórico de entradas e saídas continua inteiro: os grupos, quem entrou e quem saiu, tudo segue nos seus números e relatórios. O que não volta é o monitorador: para usar o número de novo, é criar outro e ler o QR code. Isso não tem desfazer.`,
  },
};

interface ConfirmDialogProps {
  confirm: Confirm | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (confirm: Confirm) => void;
}

/**
 * Confirmação simples, não a de digitar o nome: aquela é reservada a remover
 * membro e transferir propriedade (ver `ConfirmByNameDialog`). Aqui excluir já
 * exige arquivar antes, o que é um segundo passo por construção — e confirmação
 * em fluxo repetido vira tapinha automático.
 */
function ConfirmDialog({ confirm, pending, onClose, onConfirm }: ConfirmDialogProps) {
  const copy = confirm !== null ? CONFIRM_COPY[confirm.kind] : null;

  return (
    <Dialog
      open={confirm !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={copy?.title ?? ''}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={pending}
            onClick={() => {
              if (confirm !== null) onConfirm(confirm);
            }}
          >
            {copy?.label ?? 'Confirmar'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">
        {confirm !== null && copy !== null ? copy.body(confirm.monitor.name) : null}
      </p>
    </Dialog>
  );
}
